import { useMemo, useState } from 'react';

import { ArrowDown, ArrowUp, ArrowUpDown, ChevronRight, Search, Star, Trophy } from 'lucide-react';

import { MOCK_LUMINAIRES, MOCK_SERIES } from '../../../mocks/luminaireAnalytics';
import { MockLuminaire } from '../../../types/analytics';
import { LampThumb } from './LampThumb';

interface MyLuminairesTabProps {
  /** Open the Luminaire Performance view for a given luminaire (places it in Slot A). */
  onOpenPerformance: (luminaireId: string) => void;
}

type SortKey = 'name' | 'series' | 'compatibility' | 'used' | 'favorites' | 'top3' | 'top100' | 'firstSeen';
type SortDir = 'asc' | 'desc';

const formatNum = (n: number): string => {
  if (!n) return '0';
  if (n >= 10000) return `${(n / 1000).toFixed(1)}k`;
  return n.toLocaleString();
};

interface HeaderCell {
  key: SortKey;
  label: string;
  align: 'left' | 'right';
  hint?: string;
  iconBefore?: React.ReactNode;
}

const COLUMNS: HeaderCell[] = [
  { key: 'name', label: 'Luminaire', align: 'left' },
  { key: 'series', label: 'Series / Product', align: 'left' },
  { key: 'compatibility', label: 'Compat.', align: 'right', hint: 'Compatibility score vs TAMAN optimal' },
  { key: 'used', label: 'Used', align: 'right', hint: '"Apply" clicks — planner picked this luminaire' },
  {
    key: 'favorites',
    label: 'Favorites',
    align: 'right',
    hint: 'Times added to favorites',
    iconBefore: <Star size={11} className="text-orange-400" />,
  },
  {
    key: 'top3',
    label: 'Top-3',
    align: 'right',
    hint: 'Appearances in top-3 Market Match results (high-intent surface)',
    iconBefore: <Trophy size={11} className="text-green01" />,
  },
  { key: 'top100', label: 'Top-100', align: 'right', hint: 'Appearances in top-100 Market Match results' },
];

export const MyLuminairesTab = ({ onOpenPerformance }: MyLuminairesTabProps) => {
  const [search, setSearch] = useState('');
  const [seriesId, setSeriesId] = useState<string>('all');
  const [sortKey, setSortKey] = useState<SortKey>('top3');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const rows = useMemo(() => {
    let list: MockLuminaire[] = MOCK_LUMINAIRES;
    if (seriesId !== 'all') list = list.filter((l) => l.seriesId === seriesId);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (l) =>
          l.name.toLowerCase().includes(q) ||
          l.sku.toLowerCase().includes(q) ||
          l.opticCode.toLowerCase().includes(q) ||
          l.product.toLowerCase().includes(q) ||
          l.series.toLowerCase().includes(q),
      );
    }
    const sorted = [...list].sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'series':
          cmp = a.series.localeCompare(b.series) || a.product.localeCompare(b.product);
          break;
        case 'compatibility':
          cmp = a.analytics.compatibilityScoreAvg - b.analytics.compatibilityScoreAvg;
          break;
        case 'used':
          cmp = a.analytics.applyCount - b.analytics.applyCount;
          break;
        case 'favorites':
          cmp = a.analytics.favoriteCount - b.analytics.favoriteCount;
          break;
        case 'top3':
          cmp = a.analytics.top3MatchCount - b.analytics.top3MatchCount;
          break;
        case 'top100':
          cmp = a.analytics.top100MatchCount - b.analytics.top100MatchCount;
          break;
        case 'firstSeen':
          cmp = +new Date(a.analytics.firstSeenAt) - +new Date(b.analytics.firstSeenAt);
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [search, seriesId, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'name' || key === 'series' ? 'asc' : 'desc');
    }
  };

  const SortIcon = ({ active, dir }: { active: boolean; dir: SortDir }) => {
    if (!active) return <ArrowUpDown size={11} className="text-greyP3" />;
    return dir === 'asc' ? (
      <ArrowUp size={11} className="text-green01" />
    ) : (
      <ArrowDown size={11} className="text-green01" />
    );
  };

  const totals = useMemo(
    () => ({
      count: rows.length,
      used: rows.reduce((acc, r) => acc + r.analytics.applyCount, 0),
      favorites: rows.reduce((acc, r) => acc + r.analytics.favoriteCount, 0),
      top3: rows.reduce((acc, r) => acc + r.analytics.top3MatchCount, 0),
      top100: rows.reduce((acc, r) => acc + r.analytics.top100MatchCount, 0),
    }),
    [rows],
  );

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Filter bar */}
      <div className="px-4 md:px-6 py-3 border-b border-greyP4 bg-blackP2/60 flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex items-center bg-blackP1 border border-greyP4 rounded-full px-3 md:w-[320px]">
          <Search size={14} className="text-greyP3" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search SKU, name, optic…"
            className="bg-transparent outline-none flex-1 px-2 py-2 text-sm text-white placeholder:text-greyP3"
          />
        </div>
        <select
          value={seriesId}
          onChange={(e) => setSeriesId(e.target.value)}
          className="bg-blackP1 border border-greyP4 rounded-full px-3 py-2 text-sm text-white outline-none hover:border-green01/50 focus:border-green01"
        >
          <option value="all">All series</option>
          {MOCK_SERIES.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <div className="flex-1" />
        <div className="flex items-center gap-4 text-[11px] text-greyP2 whitespace-nowrap">
          <span>
            <span className="text-white font-semibold">{totals.count}</span> luminaires
          </span>
          <span className="text-greyP3">·</span>
          <span>
            <span className="text-white font-semibold">{formatNum(totals.used)}</span> used
          </span>
          <span>
            <span className="text-white font-semibold">{formatNum(totals.favorites)}</span> favorites
          </span>
          <span>
            <span className="text-green01 font-semibold">{formatNum(totals.top3)}</span> top-3
          </span>
          <span>
            <span className="text-white font-semibold">{formatNum(totals.top100)}</span> top-100
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10">
            <tr className="bg-greyP5">
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className={`px-3 py-2.5 text-[11px] uppercase tracking-wider font-semibold text-greyP2 ${
                    col.align === 'right' ? 'text-right' : 'text-left'
                  }`}
                  title={col.hint}
                >
                  <button
                    onClick={() => handleSort(col.key)}
                    className={`inline-flex items-center gap-1.5 hover:text-white transition-colors ${
                      col.align === 'right' ? 'flex-row-reverse' : ''
                    }`}
                  >
                    <SortIcon active={sortKey === col.key} dir={sortDir} />
                    {col.iconBefore}
                    {col.label}
                  </button>
                </th>
              ))}
              <th className="px-3 py-2.5 text-[11px] uppercase tracking-wider font-semibold text-greyP2 text-right">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={COLUMNS.length + 1} className="px-6 py-12 text-center text-sm text-greyP2">
                  No luminaires match these filters.
                </td>
              </tr>
            )}
            {rows.map((l) => {
              const a = l.analytics;
              const isUnused = a.matchFrequencyTotal === 0;
              return (
                <tr
                  key={l.id}
                  className={`border-t border-greyP4/60 hover:bg-greyP5/40 transition-colors group ${
                    isUnused ? 'opacity-60' : ''
                  }`}
                >
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-3">
                      <LampThumb luminaire={l} size={40} />
                      <div className="min-w-0">
                        <div className="text-white font-medium truncate flex items-center gap-2">
                          {l.name}
                          {l.segment === 'flagship' && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-green01 text-black font-bold uppercase tracking-wider">
                              Flagship
                            </span>
                          )}
                          {l.segment === 'new' && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full border border-green01 text-green01 font-bold uppercase tracking-wider">
                              New
                            </span>
                          )}
                          {isUnused && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded-full border border-greyP3 text-greyP2 font-bold uppercase tracking-wider">
                              Unused
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-greyP2 truncate">
                          {l.opticCode} · {l.power}W · {l.colorTemperature}K ·{' '}
                          {Math.round(l.lumenOutput).toLocaleString()} lm · CRI {l.cri}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-[12px]">
                    <div className="text-greyP1">{l.series}</div>
                    <div className="text-[10px] text-greyP3 truncate max-w-[200px]">{l.product}</div>
                  </td>
                  <td className="px-3 py-3 text-right">
                    <span
                      className={`font-semibold ${a.compatibilityScoreAvg >= 75 ? 'text-green01' : a.compatibilityScoreAvg >= 50 ? 'text-white' : 'text-greyP2'}`}
                    >
                      {a.compatibilityScoreAvg.toFixed(0)}%
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right text-white font-medium tabular-nums">
                    {formatNum(a.applyCount)}
                  </td>
                  <td className="px-3 py-3 text-right text-white font-medium tabular-nums">
                    {formatNum(a.favoriteCount)}
                  </td>
                  <td className="px-3 py-3 text-right tabular-nums">
                    <span className="text-green01 font-semibold">{formatNum(a.top3MatchCount)}</span>
                  </td>
                  <td className="px-3 py-3 text-right text-white font-medium tabular-nums">
                    {formatNum(a.top100MatchCount)}
                  </td>
                  <td className="px-3 py-3 text-right">
                    <button
                      onClick={() => onOpenPerformance(l.id)}
                      className="inline-flex items-center gap-1 text-[11px] font-medium px-2.5 py-1 rounded-full border border-greyP4 bg-greyP5/60 text-white hover:border-green01/60 hover:text-green01 transition-colors opacity-70 group-hover:opacity-100"
                    >
                      Performance
                      <ChevronRight size={12} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
